import { Injectable } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { filter } from "rxjs/operators";
import { NovaNavTarget } from "./nova-companion-chat.model";
import { NovaRealtimeSessionService } from "./nova-realtime-session.service";

/**
 * Top-level route prefixes Nova actually has a name for. Matched the same way
 * `BackNavigationService.SELF_NAVIGATING_PREFIXES` is - an exact path or a `/prefix/…`
 * descendant, so `/score/stars-and-shadows` counts as Score but `/score-old` would not.
 *
 * Deliberately narrower than the app's full route table. A route with no entry here (History,
 * Brainbites, Forms, card-library, onboarding, the account pages, …) reports NO area rather than
 * guessing at a label Nova was never taught - a missing update is invisible, a wrong one is a
 * fact Nova now believes about where the user is.
 */
const AREA_ROUTE_PREFIXES: ReadonlyArray<readonly [string, NovaNavTarget]> = [
  ["/score", "Score"],
  ["/plan-home", "Plan"],
  ["/plan", "Plan"],
  ["/hand", "Plan"],
  ["/sanctuary", "Sanctuary"],
  ["/learn", "Learn"],
  ["/community", "Community"],
  ["/landing-page", "Home"],
  ["/settings", "Settings"],
];

/**
 * Tells Nova, silently, which area of the app the user is now in - independent of whether a
 * walkthrough (B6) is active. B6's own step delivery already carries a `screen` per step; this is
 * the GENERAL case the B5 architecture sheet asks for, so a real screen change reaches Nova
 * whether or not a walkthrough happens to be running.
 *
 * Injected in `AppComponent`'s constructor, the same way `BackNavigationService` is, so tracking
 * starts at app boot rather than whenever a Nova-related component happens to be on screen.
 *
 * WHETHER NOVA SAYS ANYTHING ABOUT THIS IS NOT THIS SERVICE'S DECISION. Per the Nova Architecture
 * Brief's invariant ("update Nova's context ≠ make Nova speak", Section 2), this only ever calls
 * `sendSilentContextUpdate` - never `sendFunctionCallOutput` or anything else that triggers
 * `response.create`. Whether Nova remarks on a screen change at all is deferred to the still-open
 * `NovaCore` rule the B5 punch list calls for; wiring this through ahead of that rule existing is
 * safe only because this service is, by construction, incapable of making her speak.
 *
 * NOT RESENT ON SESSION START. A voice session that starts mid-visit to some area sees no update
 * for the area the user was already on when it connected - `StartRealtimeSession`'s own initial
 * context is a separate, server-side concern, not this service's.
 */
@Injectable({ providedIn: "root" })
export class NovaNavigationContextService {
  /**
   * The area last reported, so sub-navigation within the same area (`/score` → `/score/…`, or a
   * query-param-only change) is not reported again. Mirrors how `BackNavigationService` dedupes
   * on the normalized path rather than the raw URL.
   */
  private lastArea: NovaNavTarget | null = null;

  constructor(private router: Router, private session: NovaRealtimeSessionService) {
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => this.onNavigationEnd(event));
  }

  private onNavigationEnd(event: NavigationEnd): void {
    const area = this.areaOf(event.urlAfterRedirects);
    const changed = area !== null && area !== this.lastArea;

    // Logged for EVERY navigation, not just ones that trigger a send - otherwise a route change
    // that resolves to an unmapped path, or stays inside the same area, looks indistinguishable
    // from this service never having seen it at all.
    // eslint-disable-next-line no-console

    if (!changed) {
      return;
    }
    this.lastArea = area;

    // No live session, no channel to write to - `sendClientEvent` would no-op anyway, but
    // there is no reason to build an item for nobody.
    if (!this.session.isActive) {
      return;
    }

    const sent = this.session.sendSilentContextUpdate({
      type: "message",
      role: "system",
      content: [{ type: "input_text", text: `The user is now on the ${area} screen.` }],
    });

    // eslint-disable-next-line no-console
  }

  /** Path only - query string and fragment stripped, matching `BackNavigationService.normalize`. */
  private areaOf(url: string): NovaNavTarget | null {
    const path = url.split("?")[0].split("#")[0];
    const match = AREA_ROUTE_PREFIXES.find(
      ([prefix]) => path === prefix || path.startsWith(`${prefix}/`)
    );
    return match ? match[1] : null;
  }
}
